/**
 * How many keys a panel has and where they sit.
 *
 * The grids are the reference app's own layouts (`KEYPAD_TYPES` in
 * rcu_config/frontend/src/lib/constants/keypad.ts), so a panel programmed here
 * matches the engraving sheet a fitter has in hand. Rows and columns are 1-based
 * and the canvas lays keys out from them; a cell exists only where the panel has
 * a key, which is why the list is not simply row-major.
 */
export const KEY_LAYOUT = {
  KEYS_1: 'keys1',
  KEYS_2: 'keys2',
  KEYS_3: 'keys3',
  KEYS_4: 'keys4',
  KEYS_6: 'keys6',
  CORRIDOR: 'corridor4',
} as const;

export type KeyLayoutKey = (typeof KEY_LAYOUT)[keyof typeof KEY_LAYOUT];

/** One key's place on the panel face. `index` is 0-based; the wire counts from one. */
export interface KeyCell {
  readonly index: number;
  readonly row: number;
  readonly col: number;
}

export interface KeyLayoutSpec {
  readonly label: string;
  readonly rows: number;
  readonly cols: number;
  readonly cells: readonly KeyCell[];
}

export const KEY_LAYOUT_SPECS = {
  [KEY_LAYOUT.KEYS_1]: {
    label: '1-key',
    rows: 3,
    cols: 3,
    cells: [{ index: 0, row: 2, col: 2 }],
  },
  [KEY_LAYOUT.KEYS_2]: {
    label: '2-key',
    rows: 3,
    cols: 3,
    cells: [
      { index: 0, row: 2, col: 1 },
      { index: 1, row: 2, col: 3 },
    ],
  },
  [KEY_LAYOUT.KEYS_3]: {
    label: '3-key',
    rows: 2,
    cols: 3,
    cells: [
      { index: 0, row: 1, col: 2 },
      { index: 1, row: 2, col: 1 },
      { index: 2, row: 2, col: 3 },
    ],
  },
  [KEY_LAYOUT.KEYS_4]: {
    label: '4-key',
    rows: 2,
    cols: 2,
    cells: [
      { index: 0, row: 2, col: 2 },
      { index: 1, row: 2, col: 1 },
      { index: 2, row: 1, col: 2 },
      { index: 3, row: 1, col: 1 },
    ],
  },
  [KEY_LAYOUT.KEYS_6]: {
    label: '6-key',
    rows: 3,
    cols: 2,
    cells: [
      { index: 0, row: 3, col: 2 },
      { index: 1, row: 3, col: 1 },
      { index: 2, row: 2, col: 2 },
      { index: 3, row: 2, col: 1 },
      { index: 4, row: 1, col: 2 },
      { index: 5, row: 1, col: 1 },
    ],
  },
  [KEY_LAYOUT.CORRIDOR]: {
    label: '4-key corridor',
    rows: 2,
    cols: 2,
    cells: [
      { index: 0, row: 1, col: 1 },
      { index: 1, row: 1, col: 2 },
      { index: 2, row: 2, col: 1 },
      { index: 3, row: 2, col: 2 },
    ],
  },
} as const satisfies Record<KeyLayoutKey, KeyLayoutSpec>;

/** The keys a layout carries, read from the cells so the two cannot disagree. */
export const keyCountOf = (layout: KeyLayoutKey): number => KEY_LAYOUT_SPECS[layout].cells.length;

/**
 * The firmware numbers a panel's keys from one: `Key 1` is button 1 on the wire,
 * which is also the number engraved on the face. The layout keeps a 0-based cell
 * index internally, so the conversion happens here once — the reference app's
 * export, its stored `key_index`, and the corridor bell at key 4 all agree that
 * nothing on the wire starts at zero.
 */
export const KEY_NUMBERING = {
  WIRE_FIRST: 1,
} as const;

/** The firmware's number for the key at a 0-based face index. */
export const keyNumberOfIndex = (index: number): number => index + KEY_NUMBERING.WIRE_FIRST;

/** The cell a key sits in, or null when the index is not on this layout. */
export const cellOf = (layout: KeyLayoutKey, index: number): KeyCell | null =>
  KEY_LAYOUT_SPECS[layout].cells.find((cell) => cell.index === index) ?? null;
